import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, throwError, map, of } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { User, UsersResponse, UpdateUserResponse } from '../models/user.model';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';
import { getSecureRandomInt } from '../utils/secure-random.util';

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  private http = inject(HttpClient);
  private oauthService = inject(OAuthService, { optional: true });
  private authService = inject(AuthService);
  
  // Endpoint base desde environment
  private readonly baseUrl = environment.userManagementApiUrl;
  private readonly usersEndpoint = `${this.baseUrl}/users/`;
  private readonly chatbotsEndpoint = `${this.baseUrl}/chatbot`;

  private currentUsername: string = '';

  constructor() {
    // Suscribirse al usuario autenticado para obtener el email
    this.authService.user$.subscribe(user => {
      if (user && user.email) {
        this.currentUsername = user.email;
      }
    });
  }

  /**
   * Get all users with their associated chatbots
   */
  getUsers(): Observable<User[]> {
    if (environment.mockAuth) {
      return this.getMockUsers();
    }
    
    const headers = this.getAuthHeaders();
    
    return this.http.get<UsersResponse>(this.usersEndpoint, { headers }).pipe(
      map(response => response.users),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  private getMockUsers(): Observable<User[]> {
    const mockUsers: User[] = [
      {
        userId: 1,
        email: 'user1@test.com',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'admin',
        updatedBy: null,
        chatbots: [
          {
            chatbotId: 1,
            name: 'Test Chatbot 1',
            code: 'test-bot-1',
            description: 'First test chatbot',
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: 'admin',
            updatedBy: null,
            endpointToSend: '',
            endpointToReceive: '',
            credentials: null,
            typeId: 1,
            conversationVisualizerEndpoint: null,
            conversationVisualizerCredentials: null,
          }
        ]
      },
      {
        userId: 2,
        email: 'user2@test.com',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'admin',
        updatedBy: null,
        chatbots: [
          {
            chatbotId: 2,
            name: 'Test Chatbot 2',
            code: 'test-bot-2',
            description: 'Second test chatbot',
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: 'admin',
            updatedBy: null,
            endpointToSend: '',
            endpointToReceive: '',
            credentials: null,
            typeId: 1,
            conversationVisualizerEndpoint: null,
            conversationVisualizerCredentials: null,
          }
        ]
      }
    ];
    return of(mockUsers);
  }

  /**
   * Update user information
   */
  updateUser(userId: number, userData: Partial<User>, chatbotIds?: number[]): Observable<User> {
    if (environment.mockAuth) {
      return this.getMockUpdatedUser(userId, userData, chatbotIds);
    }
    
    const headers = this.getAuthHeaders();
    const url = `${this.usersEndpoint}${userId}`;
    
    const payload: any = {
      email: userData.email,
      operatedBy: this.currentUsername,
      isActive: userData.isActive
    };
    
    // Si se proporcionan IDs de chatbots, agregarlos al payload
    if (chatbotIds !== undefined) {
      payload.chatbotIds = chatbotIds;
    }
    
    return this.http.put<UpdateUserResponse>(url, payload, { headers }).pipe(
      map(response => response.user),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  private getMockUpdatedUser(userId: number, userData: Partial<User>, chatbotIds?: number[]): Observable<User> {
    const mockUser: User = {
      userId,
      email: userData.email || 'updated@test.com',
      isActive: userData.isActive ?? true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'admin',
      updatedBy: this.currentUsername,
      chatbots: []
    };
    return of(mockUser);
  }

  /**
   * Create new user
   */
  createUser(userData: Partial<User>, chatbotIds?: number[]): Observable<User> {
    if (environment.mockAuth) {
      return this.getMockCreatedUser(userData, chatbotIds);
    }
    
    const headers = this.getAuthHeaders();
    
    const payload: any = {
      email: userData.email,
      operatedBy: this.currentUsername,
      isActive: userData.isActive ?? true
    };
    
    // Si se proporcionan IDs de chatbots, agregarlos al payload
    if (chatbotIds !== undefined) {
      payload.chatbotIds = chatbotIds;
    }
    
    return this.http.post<UpdateUserResponse>(this.usersEndpoint, payload, { headers }).pipe(
      map(response => response.user),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  private getMockCreatedUser(userData: Partial<User>, chatbotIds?: number[]): Observable<User> {
    const mockUser: User = {
      userId: getSecureRandomInt(100, 1100),
      email: userData.email || 'newuser@test.com',
      isActive: userData.isActive ?? true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: this.currentUsername,
      updatedBy: null,
      chatbots: []
    };
    return of(mockUser);
  }

  /**
   * Get authorization headers with OAuth token
   */
  private getAuthHeaders(): HttpHeaders {
    const token = this.oauthService?.getAccessToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }
}

